/**
 * Announce a message to screen readers using ARIA live regions
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  const liveRegion = document.getElementById(`aria-live-${priority}`);
  if (liveRegion) {
    liveRegion.textContent = message;
    // Clear after announcement to avoid repeated readings
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);
  }
}

/**
 * Trap focus within a container element (useful for modals)
 */
export function trapFocus(element: HTMLElement): () => void {
  const focusableElements = element.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  if (focusableElements.length === 0) {
    return () => {}; // No-op cleanup function
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  function handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }

    // Escape key to close (if needed)
    if (e.key === 'Escape') {
      const event = new CustomEvent('escape');
      element.dispatchEvent(event);
    }
  }

  element.addEventListener('keydown', handleKeyDown);

  // Focus the first element initially
  firstElement.focus();

  // Return cleanup function
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
}

/**
 * Get a user-friendly message for connection status
 */
export function getConnectionStatusMessage(status: string): string {
  switch (status) {
    case 'connected':
      return 'Connected to server';
    case 'connecting':
      return 'Connecting to server...';
    case 'reconnecting':
      return 'Connection lost. Reconnecting...';
    case 'disconnected':
      return 'Disconnected from server';
    case 'error':
      return 'Connection error occurred';
    default:
      return 'Unknown connection status';
  }
}
